import React, { useState, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
// import 'leaflet/dist/leaflet.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, X } from 'lucide-react';
import { useTheme } from '../../../ThemeContext';
import { regions } from '../data/mockData';
import L from 'leaflet';

// Update the store icon to include ranking
const createStoreIcon = (ranking) => new L.divIcon({
  html: `
    <div class="relative">
      <div class="w-8 h-8 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-500">
        <svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 3h18v6H3zM3 9h18l-9 9z"/>
        </svg>
      </div>
      <div class="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
        ${ranking}
      </div>
    </div>`,
  className: 'store-icon',
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});

// Add this new component for handling zoom
const RegionZoom = ({ center, isSelected }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (isSelected) {
      map.setView(center, 9, {
        animate: true,
        duration: 1
      });
    }
  }, [isSelected, center, map]);

  return null;
};

const MapView = ({ timeRange }) => {
  const { isDarkMode = false } = useTheme() || {};
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [mapView, setMapView] = useState('sales');
  const mapRef = useRef(null);

  // Calculate circle radius based on sales
  const getCircleRadius = (sales) => {
    const minRadius = 20;
    const maxRadius = 50;
    const minSales = Math.min(...regions.map(r => r.totalSales));
    const maxSales = Math.max(...regions.map(r => r.totalSales));
    
    return minRadius + ((sales - minSales) / (maxSales - minSales)) * (maxRadius - minRadius);
  };

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
  };

  const handleResetView = () => {
    setSelectedRegion(null);
    setSelectedStore(null);
    mapRef.current?.setView([24.7136, 46.6753], 6, {
      animate: true,
      duration: 1
    });
  };

  const getAllStoresSorted = () => {
    const allStores = regions.flatMap(region => 
      region.stores.map(store => ({
        ...store,
        region: region.name
      }))
    );
    return allStores.sort((a, b) => b.sales - a.sales);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Regional Performance</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMapView('sales')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  mapView === 'sales' 
                    ? 'bg-blue-50 text-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Sales
              </button>
              <button
                onClick={() => setMapView('growth')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  mapView === 'growth' 
                    ? 'bg-green-50 text-green-600' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Growth
              </button>
              <button
                onClick={() => setMapView('stores')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  mapView === 'stores' 
                    ? 'bg-purple-50 text-purple-600' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Stores
              </button>
            </div>
            {selectedRegion && (
              <button
                onClick={handleResetView}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Reset View
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <MapContainer
          ref={mapRef}
          center={[24.7136, 46.6753]}
          zoom={6}
          style={{ height: '600px' }}
          className="z-0"
        >
          <TileLayer
            url={isDarkMode 
              ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            }
          />
          
          {regions.map(region => (
            <CircleMarker
              key={region.id}
              center={region.center}
              radius={getCircleRadius(region.totalSales)}
              fillColor={mapView === 'sales' ? '#3b82f6' : mapView === 'growth' ? '#10b981' : '#8b5cf6'}
              color={isDarkMode ? '#ffffff' : '#1f2937'}
              weight={1}
              fillOpacity={0.7}
              eventHandlers={{
                click: () => handleRegionClick(region),
                mouseover: (e) => {
                  e.target.setStyle({ fillOpacity: 0.9 });
                },
                mouseout: (e) => {
                  e.target.setStyle({ fillOpacity: 0.7 });
                }
              }}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-semibold">{region.name}</h3>
                  <p>Sales: SAR {region.totalSales.toLocaleString()}</p>
                  <p>Growth: {region.growth}%</p>
                  <p>Stores: {region.storeCount}</p>
                </div>
              </Popup>
            </CircleMarker>
          ))}

          {selectedRegion && (
            <RegionZoom 
              center={selectedRegion.center}
              isSelected={true}
            />
          )}

          {/* Store Markers */}
          {selectedRegion && selectedRegion.stores.map(store => {
            const ranking = getAllStoresSorted().findIndex(s => s.id === store.id) + 1;
            return (
              <Marker
                key={store.id}
                position={store.coordinates}
                icon={createStoreIcon(ranking)}
                eventHandlers={{
                  click: () => setSelectedStore(store)
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 className="font-semibold">{store.name}</h3>
                    <p className="text-sm text-blue-600 font-medium">Rank #{ranking}</p>
                    <p>Sales: SAR {store.sales.toLocaleString()}</p>
                    <p>Growth: {store.growth}%</p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Selected Region Details */}
      <AnimatePresence>
        {selectedRegion && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedRegion.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedRegion.storeCount} Stores
                </p>
              </div>
              <button
                onClick={() => setSelectedRegion(null)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stores Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedRegion.stores.map(store => (
                <div
                  key={store.id}
                  className={`p-4 rounded-lg border ${
                    selectedStore?.id === store.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {store.name}
                      </h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-gray-500 dark:text-gray-400">
                          Sales: SAR {store.sales.toLocaleString()}
                        </p>
                        <p className={`${
                          store.growth >= 0 
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          Growth: {store.growth}%
                        </p>
                      </div>
                    </div>
                    <Store className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MapView; 